import { FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, auth } from '../lib/api';
import AuthBrand from '../components/AuthBrand';
import PasswordInput from '../components/PasswordInput';
import { LEGAL_LINKS } from '../components/shell/legal';

interface LoginResponse {
  accessToken?: string;
  /** PASSWORD_SETUP_REQUIRED — pipeline-provisioned account with no password
   * yet; the server has already emailed a set-password link. */
  state?: string;
}

/**
 * Same-origin sign-in (pattern proven in v1 commit 42992ab): POST /auth/login,
 * store the token, route by flow state. First login → mandatory password reset.
 */
export default function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Redirects into sign-in (e.g. an already-activated onboarding link) can pass
  // a one-line explanation to show above the form.
  const [info, setInfo] = useState<string | null>(
    (location.state as { info?: string } | null)?.info ?? null,
  );
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api<LoginResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      if (res.state === 'PASSWORD_SETUP_REQUIRED' || !res.accessToken) {
        setInfo(
          "Your account doesn't have a password yet. We've emailed you a secure link to set one — check your inbox, then come back and sign in.",
        );
        return;
      }
      auth.setToken(res.accessToken);
      // /auth/login never returns a flowState — the only other branch this
      // used to take, '/auth/password-reset', targeted a route that has
      // never existed in App.tsx (dead code; see Asana 1216297303033553).
      navigate('/dashboard');
    } catch {
      setError('Invalid email or password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <AuthBrand title="Application Portal" subtitle="Sign in to continue your journey" />
      <form className="auth-card" onSubmit={onSubmit}>
        <label>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" />
        </label>
        <label>
          Password
          <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
        </label>
        {error && <p role="alert">{error}</p>}
        {info && <p role="status">{info}</p>}
        <button type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <p style={{ marginTop: 12, textAlign: 'center' }}>
          <a href="/forgot-password">Forgot your password?</a>
        </p>
      </form>
      <nav className="auth-legal" aria-label="Legal">
        {LEGAL_LINKS.map(({ label, to }) => (
          <Link key={label} to={to}>{label}</Link>
        ))}
      </nav>
    </main>
  );
}
